import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData } from '../tokens/types';
import { PdfSection, PdfChip } from '../primitives/PdfPrimitives';
import { ScreenSection, ScreenChip } from '../primitives/ScreenPrimitives';
import { PdfStatGrid, ScreenStatGrid, PdfMiniBars, ScreenMiniBars, PdfDonut, ScreenDonut } from '../primitives/ChartPrimitives';
import { colors, fontFamily, space, typeScale } from '../tokens/designTokens';

const truncate = (s?: string, max = 220): string | undefined => {
    if (!s) return undefined;
    return s.length > max ? s.slice(0, max).trim() + '…' : s;
};

const statusTone = (status: string) => (status === 'OPEN' ? 'HIGH' : 'LOW');

const PdfMcaCharges: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const mc = data.mcaCharges;
    if (!mc) return null;
    const byHolder = new Map<string, number>();
    mc.rows.filter((r) => r.status === 'OPEN').forEach((r) => byHolder.set(r.holder, (byHolder.get(r.holder) ?? 0) + r.amountNumeric));
    const donutSlices = Array.from(byHolder.entries()).map(([label, value]) => ({ label, value }));
    const barItems = mc.rows.slice(0, 6).map((r) => ({
        label: r.holder,
        value: r.amountNumeric,
        valueLabel: r.amount,
    }));

    return (
        <PdfSection
            kicker="Section · Charges"
            title="MCA charges"
            subtitle="Creditor-wise open & closed charges filed against the company."
            index={index}
            breakBefore={breakBefore}
        >
            <PdfStatGrid
                items={[
                    { label: 'Total charges', value: String(mc.rows.length) },
                    { label: 'Open charges', value: String(mc.totalOpen), deltaTone: mc.totalOpen > 0 ? 'neg' : 'pos' },
                    { label: 'Open exposure', value: mc.totalOpenAmountLabel },
                    { label: 'Closed', value: String(mc.totalClosed) },
                ]}
            />

            {donutSlices.length > 0 ? (
                <View style={{ marginTop: space.md, padding: space.md, borderRadius: 4, backgroundColor: colors.surfaceAlt }}>
                    <Text
                        style={{
                            fontSize: typeScale.micro,
                            letterSpacing: 1,
                            color: colors.muted,
                            textTransform: 'uppercase',
                            marginBottom: 3,
                            fontFamily: fontFamily.sansBold,
                        }}
                    >
                        Open exposure by creditor
                    </Text>
                    <Text style={{ fontSize: typeScale.micro, color: colors.mutedSoft, marginBottom: 6, lineHeight: 1.4 }}>
                        Share of total outstanding (open) charge amount held by each lender.
                    </Text>
                    <PdfDonut slices={donutSlices} size={90} />
                </View>
            ) : null}

            <View style={{ marginTop: space.md }}>
                <Text
                    style={{
                        fontSize: typeScale.micro,
                        letterSpacing: 1,
                        color: colors.muted,
                        textTransform: 'uppercase',
                        marginBottom: 6,
                        fontFamily: fontFamily.sansBold,
                    }}
                >
                    Top charges (by amount)
                </Text>
                <PdfMiniBars items={barItems} />
            </View>

            {/* 2-up card grid built as explicit row pairs (skill rule 2 — react-pdf's
                flexWrap diverges from CSS and shifts cards out of alignment when content
                lengths vary). Outer column gap from `cardGap`; vertical row gap matches.
                Card padding bumped from sp3 → sp4 because the previous density was
                cramping the title and chips ran into each other on long bank names. */}
            {(() => {
                const cardGap = 10;
                const cardPad = space.sp4;
                type Row = [(typeof mc.rows)[number] | undefined, (typeof mc.rows)[number] | undefined];
                const rows: Row[] = [];
                for (let i = 0; i < mc.rows.length; i += 2) rows.push([mc.rows[i], mc.rows[i + 1]]);
                return (
                    <View style={{ flexDirection: 'column', gap: cardGap, marginTop: space.md }}>
                        {rows.map((row, ri) => (
                            <View key={ri} style={{ flexDirection: 'row', gap: cardGap }} wrap={false}>
                                {row.map((r, ci) => {
                                    if (!r) return <View key={ci} style={{ flex: 1 }} />;
                                    const accent = r.status === 'OPEN' ? colors.neg : colors.pos;
                                    return (
                                        <View
                                            key={ci}
                                            wrap={false}
                                            style={{
                                                flex: 1,
                                                padding: cardPad,
                                                borderWidth: 0.5,
                                                borderColor: colors.rule,
                                                borderLeftWidth: 2,
                                                borderLeftColor: accent,
                                                borderRadius: 4,
                                                backgroundColor: colors.paper,
                                            }}
                                        >
                                            {/* Status chip lifted out of the title baseline row.
                                                Previously the chip sat next to the title with
                                                `alignItems: baseline` — which let the chip steal
                                                horizontal space from the holder name and clip the
                                                last word ("PRIVATE LIM…" instead of "PRIVATE
                                                LIMITED"). Pinning the chip to its own right-
                                                aligned row above the title gives the holder full
                                                card width to wrap into. */}
                                            <View
                                                style={{
                                                    flexDirection: 'row',
                                                    justifyContent: 'flex-end',
                                                    marginBottom: 4,
                                                }}
                                            >
                                                <PdfChip tone={statusTone(r.status)}>{r.status}</PdfChip>
                                            </View>
                                            <Text
                                                style={{
                                                    fontSize: typeScale.bodySm,
                                                    fontFamily: fontFamily.sansBold,
                                                    color: colors.ink00,
                                                    lineHeight: 1.3,
                                                    marginBottom: 8,
                                                }}
                                            >
                                                {r.holder}
                                            </Text>
                                            <View style={{ flexDirection: 'row', gap: 8 }}>
                                                <View style={{ flex: 1 }}>
                                                    <Text
                                                        style={{
                                                            fontSize: typeScale.micro,
                                                            color: colors.ink40,
                                                            letterSpacing: 0.5,
                                                            textTransform: 'uppercase',
                                                        }}
                                                    >
                                                        Charge amount
                                                    </Text>
                                                    <Text
                                                        style={{
                                                            fontSize: typeScale.bodySm,
                                                            fontFamily: fontFamily.sansBold,
                                                            color: colors.ink00,
                                                            marginTop: 2,
                                                        }}
                                                    >
                                                        {r.amount || '—'}
                                                    </Text>
                                                </View>
                                                <View style={{ flex: 1 }}>
                                                    <Text
                                                        style={{
                                                            fontSize: typeScale.micro,
                                                            color: colors.ink40,
                                                            letterSpacing: 0.5,
                                                            textTransform: 'uppercase',
                                                        }}
                                                    >
                                                        Filed on
                                                    </Text>
                                                    <Text style={{ fontSize: typeScale.bodySm, color: colors.ink00, marginTop: 2 }}>
                                                        {r.creationDate}
                                                    </Text>
                                                </View>
                                            </View>
                                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                                                {r.rateOfInterest ? <PdfChip tone="muted">RoI {r.rateOfInterest}</PdfChip> : null}
                                                {r.numberOfHolders && r.numberOfHolders !== '1' ? (
                                                    <PdfChip tone="muted">{r.numberOfHolders} holders</PdfChip>
                                                ) : null}
                                                {r.modificationDate ? <PdfChip tone="muted">Mod. {r.modificationDate}</PdfChip> : null}
                                                {r.propertyType && r.propertyType.length <= 32 ? (
                                                    <PdfChip tone="muted">{r.propertyType}</PdfChip>
                                                ) : null}
                                            </View>
                                            {r.propertyType && r.propertyType.length > 32 ? (
                                                <Text
                                                    style={{
                                                        // Bumped from 5.5 → 7pt — the previous size was
                                                        // genuinely unreadable, especially when the
                                                        // property type is a wall of uppercase text.
                                                        fontSize: 7,
                                                        color: colors.ink40,
                                                        letterSpacing: 0.3,
                                                        textTransform: 'uppercase',
                                                        lineHeight: 1.4,
                                                        marginTop: 6,
                                                    }}
                                                >
                                                    {r.propertyType}
                                                </Text>
                                            ) : null}
                                            {r.instrumentDesc ? (
                                                <Text
                                                    style={{
                                                        fontSize: typeScale.micro,
                                                        color: colors.inkSoft,
                                                        marginTop: 6,
                                                        lineHeight: 1.4,
                                                    }}
                                                >
                                                    {truncate(r.instrumentDesc, 120)}
                                                </Text>
                                            ) : null}
                                            {r.termsOfPayment ? (
                                                <View
                                                    style={{
                                                        marginTop: 6,
                                                        paddingTop: 4,
                                                        borderTopWidth: 0.5,
                                                        borderTopColor: colors.ruleSoft,
                                                    }}
                                                >
                                                    <Text
                                                        style={{
                                                            fontSize: typeScale.micro,
                                                            color: colors.ink40,
                                                            letterSpacing: 0.4,
                                                            textTransform: 'uppercase',
                                                            marginBottom: 2,
                                                        }}
                                                    >
                                                        Payment terms
                                                    </Text>
                                                    <Text style={{ fontSize: typeScale.micro, color: colors.inkSoft, lineHeight: 1.4 }}>
                                                        {truncate(r.termsOfPayment, 220)}
                                                    </Text>
                                                </View>
                                            ) : null}
                                            {r.propertyParticulars ? (
                                                <View style={{ marginTop: 4 }}>
                                                    <Text
                                                        style={{
                                                            fontSize: typeScale.micro,
                                                            color: colors.ink40,
                                                            letterSpacing: 0.4,
                                                            textTransform: 'uppercase',
                                                            marginBottom: 2,
                                                        }}
                                                    >
                                                        Particulars
                                                    </Text>
                                                    <Text style={{ fontSize: typeScale.micro, color: colors.inkSoft, lineHeight: 1.4 }}>
                                                        {truncate(r.propertyParticulars, 220)}
                                                    </Text>
                                                </View>
                                            ) : null}
                                        </View>
                                    );
                                })}
                            </View>
                        ))}
                    </View>
                );
            })()}
        </PdfSection>
    );
};

const ScreenMcaCharges: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const mc = data.mcaCharges;
    if (!mc) return null;
    const byHolder = new Map<string, number>();
    mc.rows.filter((r) => r.status === 'OPEN').forEach((r) => byHolder.set(r.holder, (byHolder.get(r.holder) ?? 0) + r.amountNumeric));
    const donutSlices = Array.from(byHolder.entries()).map(([label, value]) => ({ label, value }));
    const barItems = mc.rows.slice(0, 6).map((r) => ({ label: r.holder, value: r.amountNumeric, valueLabel: r.amount }));
    return (
        <ScreenSection
            kicker="Section · Charges"
            title="MCA charges"
            subtitle="Creditor-wise open & closed charges filed against the company."
            index={index}
        >
            <ScreenStatGrid
                items={[
                    { label: 'Total charges', value: String(mc.rows.length) },
                    { label: 'Open charges', value: String(mc.totalOpen), deltaTone: mc.totalOpen > 0 ? 'neg' : 'pos' },
                    { label: 'Open exposure', value: mc.totalOpenAmountLabel },
                    { label: 'Closed', value: String(mc.totalClosed) },
                ]}
            />
            {donutSlices.length > 0 ? (
                <div style={{ marginTop: 14, padding: 12, background: colors.surfaceAlt, borderRadius: 6 }}>
                    <div
                        style={{
                            fontSize: 10,
                            letterSpacing: 1,
                            color: colors.muted,
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            marginBottom: 3,
                        }}
                    >
                        Open exposure by creditor
                    </div>
                    <div style={{ fontSize: 10.5, color: colors.mutedSoft, marginBottom: 8, lineHeight: 1.4 }}>
                        Share of total outstanding (open) charge amount held by each lender.
                    </div>
                    <ScreenDonut slices={donutSlices} size={110} />
                </div>
            ) : null}
            <div style={{ marginTop: 14 }}>
                <div
                    style={{
                        fontSize: 10,
                        letterSpacing: 1,
                        color: colors.muted,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        marginBottom: 6,
                    }}
                >
                    Top charges (by amount)
                </div>
                <ScreenMiniBars items={barItems} />
            </div>
            <div style={{ marginTop: 12 }}>
                {mc.rows.map((r, i) => (
                    <div
                        key={i}
                        style={{
                            padding: '10px 0',
                            borderBottom: `0.5px solid ${colors.hairlineSoft}`,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 2, fontSize: 12.5, fontWeight: 700, color: colors.ink }}>{r.holder}</div>
                            <div style={{ flex: 1, fontSize: 11.5, textAlign: 'right', color: colors.inkSoft, fontWeight: 600 }}>
                                {r.amount}
                            </div>
                            <div style={{ flex: 1, fontSize: 11, textAlign: 'right', color: colors.muted }}>{r.creationDate}</div>
                            <div style={{ width: 70, textAlign: 'right' }}>
                                <ScreenChip tone={statusTone(r.status)}>{r.status}</ScreenChip>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                            {r.rateOfInterest ? <ScreenChip tone="muted">RoI {r.rateOfInterest}</ScreenChip> : null}
                            {r.numberOfHolders && r.numberOfHolders !== '1' ? (
                                <ScreenChip tone="muted">{r.numberOfHolders} holders</ScreenChip>
                            ) : null}
                            {r.modificationDate ? <ScreenChip tone="muted">Mod. {r.modificationDate}</ScreenChip> : null}
                            {r.propertyType && r.propertyType.length <= 32 ? <ScreenChip tone="muted">{r.propertyType}</ScreenChip> : null}
                            {r.instrumentDesc ? <ScreenChip tone="muted">{r.instrumentDesc}</ScreenChip> : null}
                        </div>
                        {r.propertyType && r.propertyType.length > 32 ? (
                            <div
                                style={{
                                    fontSize: 9,
                                    color: colors.ink40,
                                    letterSpacing: 0.3,
                                    textTransform: 'uppercase',
                                    lineHeight: 1.4,
                                    marginTop: 6,
                                }}
                            >
                                {r.propertyType}
                            </div>
                        ) : null}
                        {(r.termsOfPayment || r.propertyParticulars || r.extentAndOperation) && (
                            <details style={{ marginTop: 6 }}>
                                <summary
                                    style={{
                                        cursor: 'pointer',
                                        fontSize: 10.5,
                                        color: colors.brand,
                                        fontWeight: 700,
                                        letterSpacing: 0.4,
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    Charge terms
                                </summary>
                                <div
                                    style={{
                                        marginTop: 6,
                                        fontSize: 11.5,
                                        color: colors.inkSoft,
                                        lineHeight: 1.5,
                                        background: colors.surface,
                                        padding: 8,
                                        borderRadius: 4,
                                    }}
                                >
                                    {r.termsOfPayment ? (
                                        <div style={{ marginBottom: 4 }}>
                                            <strong>Payment terms:</strong> {r.termsOfPayment}
                                        </div>
                                    ) : null}
                                    {r.propertyParticulars ? (
                                        <div style={{ marginBottom: 4 }}>
                                            <strong>Particulars:</strong> {r.propertyParticulars}
                                        </div>
                                    ) : null}
                                    {r.extentAndOperation ? (
                                        <div style={{ marginBottom: 4 }}>
                                            <strong>Extent & operation:</strong> {r.extentAndOperation}
                                        </div>
                                    ) : null}
                                    {r.otherTerms ? (
                                        <div>
                                            <strong>Other terms:</strong> {r.otherTerms}
                                        </div>
                                    ) : null}
                                </div>
                            </details>
                        )}
                    </div>
                ))}
            </div>
        </ScreenSection>
    );
};

export const mcaChargesModule: ModuleDefinition = {
    meta: {
        id: 'mcaCharges' as any,
        title: 'MCA charges',
        description: 'Secured lender exposure, creditor concentration and open / closed status.',
        dataSource: 'polus/insights/tab/data:mcaCharges',
    },
    hasData: (d) => !!d.mcaCharges && d.mcaCharges.rows.length > 0,
    Pdf: PdfMcaCharges,
    Screen: ScreenMcaCharges,
};
